import React from "react"
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2,
  Box,
  Skeleton,
  IconButton,
  Divider,
  useTheme
} from "@mui/material"
import { Close as CloseIcon, LocationOn as LocationIcon, Public as PublicIcon } from "@mui/icons-material"

// Complete Dialog Skeleton
export const LocationFormDialogSkeleton = () => {
  const theme = useTheme()

  return (
    <>
      {/* Dialog Title Skeleton */}
      <DialogTitle
        sx={{
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          color: theme.palette.primary.contrastText,
          py: 3,
          px: 4,
          position: "relative"
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box>
            <Skeleton variant="text" width={200} height={32} sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
            <Skeleton variant="text" width={300} height={20} sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
          </Box>
        </Box>
        <IconButton
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: theme.palette.primary.contrastText
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content Skeleton */}
      <DialogContent sx={{ background: theme.palette.background.default }}>
        <Grid2 container spacing={3} sx={{ py: 4 }}>
          {/* Basic Information Section */}
          <Grid2 size={12}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <LocationIcon color="primary" />
              <Skeleton variant="text" width={200} height={32} />
            </Box>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12 }}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid2>
              <Grid2 size={12}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid2>
            </Grid2>
          </Grid2>

          {/* Location Details Section */}
          <Grid2 size={12}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <PublicIcon color="primary" />
              <Skeleton variant="text" width={150} height={32} />
            </Box>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12 }}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid2>
            </Grid2>
          </Grid2>

          {/* Tax Information Section */}
          <Grid2 size={12}>
            <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid2>
            </Grid2>
          </Grid2>

          {/* Current Selection Display Skeleton */}
          <Grid2 size={12}>
            <Skeleton
              variant="rectangular"
              height={60}
              sx={{
                borderRadius: 2,
                backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100]
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <Divider />

      {/* Dialog Actions Skeleton */}
      <DialogActions
        sx={{
          p: 3,
          background: theme.palette.background.default
        }}
      >
        <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: 1 }} />
      </DialogActions>
    </>
  )
}

// Individual Skeleton Components (for backward compatibility)
export const FormSectionSkeleton = ({ icon }: { icon: React.ReactNode }) => (
  <Grid2 size={12}>
    <Box display="flex" alignItems="center" gap={2} mb={2}>
      {icon}
      <Skeleton variant="text" width={200} height={32} />
    </Box>
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 12 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
      </Grid2>
    </Grid2>
  </Grid2>
)

export const TaxSectionSkeleton = () => (
  <Grid2 size={12}>
    <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
      </Grid2>
    </Grid2>
  </Grid2>
)

export const LocationDetailsSkeleton = () => (
  <Grid2 size={12}>
    <Box display="flex" alignItems="center" gap={2} mb={2}>
      <PublicIcon color="primary" />
      <Skeleton variant="text" width={150} height={32} />
    </Box>
    <Grid2 container spacing={2}>
      <Grid2 size={{ xs: 12 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
      </Grid2>
    </Grid2>
  </Grid2>
)

export const DialogActionsSkeleton = () => (
  <Box display="flex" gap={2} width="100%">
    <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
    <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: 1 }} />
  </Box>
)
